// src/components/NotificationBell.jsx

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../hooks/useNotifications";
import {
  doc, updateDoc, serverTimestamp, addDoc, collection,
} from "firebase/firestore";
import { db } from "../firebase";
import { notifyEnquiryFollowUp, dismissNotificationForUser, markNotificationResolved } from "../services/notificationService";

const PRIORITY_STYLES = {
  high:   { dot: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
  medium: { dot: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  low:    { dot: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
};

const TYPE_ICONS = {
  pending_acceptance:       "🔧",
  pending_send:             "📤",
  return_pending:           "↩️",
  mrf_pending:              "📋",
  project_mrf_pending:      "📋",
  expense_pending:          "💰",
  expense_approved:         "✅",
  billing_pending:          "🧾",
  invoice_created:          "💵",
  invoice_paid:             "✅",
  service_assigned:         "🔧",
  service_approval_pending: "✅",
  service_closed:           "🎯",
  po_created:               "🛒",
  grn_completed:            "📥",
  project_milestone:        "🏆",
  low_stock:                "⚠️",
  stock_adjustment_pending: "📊",
  enquiry_followup:         "⏰",
  cash_collected:           "💵",
};

export default function NotificationBell() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const uid       = user.uid || user.id || "";
  const { notifications, totalCount, refresh, markInfoRead } = useNotifications();
  const [open, setOpen]         = useState(false);
  const [snoozeId, setSnoozeId] = useState(null); // enquiry notification id for snooze picker
  const [snoozeDate, setSnoozeDate] = useState(""); // selected snooze date
  const [snoozeSaving, setSnoozeSaving] = useState(false);
  const isNavigatingRef         = useRef(false);
  const wrapRef                 = useRef(null);

  const actionItems = notifications.filter(n => n.notification_class === "action_required");
  const infoItems   = notifications.filter(n => n.notification_class === "informational");

  useEffect(() => {
    if (!open) return;
    // Mark informational as read when bell opens
    markInfoRead();

    const handler = (e) => {
      if (isNavigatingRef.current) return;
      const portal = document.getElementById("zeno-notif-dropdown");
      const inPortal = portal && portal.contains(e.target);
      const inWrap   = wrapRef.current && wrapRef.current.contains(e.target);
      if (!inPortal && !inWrap) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, markInfoRead]);

  const handleNotifClick = async (notif) => {
    if (!notif.route) return;
    isNavigatingRef.current = true;
    setOpen(false);
    navigate(notif.route);
    setTimeout(() => { isNavigatingRef.current = false; }, 200);

    // Dismiss for THIS user only, on click — applies to BOTH
    // action_required AND informational now (previously informational was
    // auto-dismissed just by opening the bell, in markInfoRead() — that
    // silently consumed multi-role notifications before the recipient had
    // actually seen them; dismissal now only happens on the same real
    // signal as action_required: the user opened it). Per-user (not global
    // resolve) — a notification shared across multiple roles (e.g.
    // Store/SM/Manager/Owner) must keep showing for whoever hasn't clicked
    // it yet. Only applies to real "notifications" collection docs
    // (_fromFirestore) — the derived items (asset transfers, MRF-pending,
    // etc.) resolve themselves naturally once the underlying record's
    // status changes, and don't live in this collection.
    if (notif._fromFirestore && notif.id) {
      try {
        await dismissNotificationForUser(notif.id, uid, notif.company_id || user.company_id || "");
        refresh();
      } catch (e) {
        console.warn("[NotificationBell] dismiss failed:", e);
      }
    }
  };

  // ── Snooze: update follow_up_date + resolve old notif + create new notif ──
  const handleSnooze = async (notif) => {
    if (!snoozeDate || snoozeSaving) return;
    setSnoozeSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const company_id = user.company_id || "";
      if (!company_id || notif.company_id !== company_id) {
        throw new Error("Access denied — notification belongs to a different company");
      }

      // 1. Resolve old notification — routed through governed service
      // function (was a direct updateDoc, bypassing the service layer)
      await markNotificationResolved(notif.id, company_id);

      // 2. Update enquiry follow_up_date
      await updateDoc(doc(db, "enquiries", notif.reference_id), {
        follow_up_date: snoozeDate,
        updated_at:     serverTimestamp(),
        updated_by:     user.uid || user.id || "",
        updated_by_name: user.name || "",
      });

      // 3. Create new notification for new date
      await notifyEnquiryFollowUp({
        enquiry_id:     notif.reference_id,
        enquiry_no:     notif.metadata?.enquiry_no || "",
        client_name:    notif.metadata?.client_name || notif.title,
        follow_up_date: snoozeDate,
        company_id:     notif.company_id,
      });

      setSnoozeId(null);
      setSnoozeDate("");
      refresh();
    } catch (err) {
      console.error("Snooze error:", err);
    } finally {
      setSnoozeSaving(false);
    }
  };

  // ── Done: resolve notif + mark last_followup_done on enquiry ──
  const handleDone = async (notif) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const company_id = user.company_id || "";
      if (!company_id || notif.company_id !== company_id) {
        throw new Error("Access denied — notification belongs to a different company");
      }

      // 1. Resolve notification — routed through governed service
      // function (was a direct updateDoc, bypassing the service layer)
      await markNotificationResolved(notif.id, company_id);

      // 2. Mark enquiry last followup done
      await updateDoc(doc(db, "enquiries", notif.reference_id), {
        last_followup_done: true,
        last_followup_at:   new Date().toISOString().slice(0, 10),
        updated_at:         serverTimestamp(),
        updated_by:         user.uid || user.id || "",
        updated_by_name:    user.name || "",
      });

      refresh();
    } catch (err) {
      console.error("Done error:", err);
    }
  };

  const renderItem = (notif, i) => {
    const s = PRIORITY_STYLES[notif.priority] || PRIORITY_STYLES.low;
    const isAction = notif.notification_class === "action_required";
    const isFollowUp = notif.type === "enquiry_followup";
    const isSnoozing = snoozeId === notif.id;

    return (
      <div key={`${notif.id}-${i}`}
        style={{
          padding: "12px 16px", borderBottom: "1px solid #f8fafc",
          background: "#fff",
          borderLeft: isAction ? `3px solid ${s.dot}` : "3px solid transparent",
        }}
      >
        {/* TOP ROW — clickable to navigate */}
        <div
          onClick={() => !isFollowUp && handleNotifClick(notif)}
          style={{
            display: "flex", gap: "12px", alignItems: "flex-start",
            cursor: isFollowUp ? "default" : "pointer",
          }}
          onMouseEnter={e => { if (!isFollowUp) e.currentTarget.style.background = "#f8fafc"; }}
          onMouseLeave={e => { if (!isFollowUp) e.currentTarget.style.background = "#fff"; }}
        >
          <div style={{
            width: "36px", height: "36px", borderRadius: "10px",
            background: s.bg, border: `1px solid ${s.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "16px", flexShrink: 0,
          }}>
            {TYPE_ICONS[notif.type] || "🔔"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: "600", fontSize: "13px", color: "#0f172a",
              marginBottom: "2px", display: "flex", alignItems: "center", gap: "6px",
            }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {notif.title}
              </span>
              <span style={{
                width: "6px", height: "6px", borderRadius: "50%",
                background: s.dot, flexShrink: 0, display: "inline-block",
              }} />
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {notif.body}
            </div>
            {isAction && !isFollowUp && (
              <div style={{ fontSize: "10px", color: s.dot, fontWeight: "700", marginTop: "2px" }}>
                ACTION REQUIRED
              </div>
            )}
          </div>
          {!isFollowUp && (
            <span style={{ color: "#cbd5e1", fontSize: "14px", flexShrink: 0, marginTop: "2px" }}>›</span>
          )}
        </div>

        {/* FOLLOW-UP ACTIONS — Snooze + Done */}
        {isFollowUp && (
          <div style={{ marginTop: "8px", paddingLeft: "48px" }}>
            {!isSnoozing ? (
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  onClick={() => { setSnoozeId(notif.id); setSnoozeDate(""); }}
                  style={{
                    flex: 1, padding: "5px 8px", borderRadius: "8px",
                    border: "1px solid #e2e8f0", background: "#f8fafc",
                    fontSize: "11px", fontWeight: "600", color: "#475569",
                    cursor: "pointer",
                  }}>
                  📅 Snooze
                </button>
                <button
                  onClick={() => handleDone(notif)}
                  style={{
                    flex: 1, padding: "5px 8px", borderRadius: "8px",
                    border: "1px solid #bbf7d0", background: "#f0fdf4",
                    fontSize: "11px", fontWeight: "600", color: "#166534",
                    cursor: "pointer",
                  }}>
                  ✅ Done
                </button>
                <button
                  onClick={() => handleNotifClick(notif)}
                  style={{
                    flex: 1, padding: "5px 8px", borderRadius: "8px",
                    border: "1px solid #bfdbfe", background: "#eff6ff",
                    fontSize: "11px", fontWeight: "600", color: "#1e40af",
                    cursor: "pointer",
                  }}>
                  👁 View
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <input
                  type="date"
                  value={snoozeDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={e => setSnoozeDate(e.target.value)}
                  style={{
                    flex: 1, padding: "5px 8px", borderRadius: "8px",
                    border: "1px solid #e2e8f0", fontSize: "12px", outline: "none",
                  }}
                />
                <button
                  onClick={() => handleSnooze(notif)}
                  disabled={!snoozeDate || snoozeSaving}
                  style={{
                    padding: "5px 10px", borderRadius: "8px",
                    background: snoozeDate ? "#0ea5e9" : "#e2e8f0",
                    color: snoozeDate ? "#fff" : "#94a3b8",
                    border: "none", fontSize: "11px", fontWeight: "700",
                    cursor: snoozeDate ? "pointer" : "default",
                  }}>
                  {snoozeSaving ? "..." : "Set"}
                </button>
                <button
                  onClick={() => { setSnoozeId(null); setSnoozeDate(""); }}
                  style={{
                    padding: "5px 8px", borderRadius: "8px",
                    background: "none", border: "1px solid #e2e8f0",
                    fontSize: "11px", color: "#94a3b8", cursor: "pointer",
                  }}>
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes zeno-pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.15); }
        }
        .zeno-notif-dropdown {
          position: fixed !important;
          top: 60px !important;
          right: 8px !important;
          left: auto !important;
          width: calc(100vw - 16px) !important;
          max-width: 360px !important;
          max-height: 75vh;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          border: 1px solid #e2e8f0;
          z-index: 999999 !important;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
      `}</style>

      <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>

        {/* BELL */}
        <button onClick={() => setOpen(prev => !prev)}
          title={totalCount > 0 ? `${totalCount} notification(s)` : "No new notifications"}
          style={{
            position: "relative", background: open ? "#f1f5f9" : "transparent",
            border: "none", borderRadius: "50%", width: "42px", height: "42px",
            cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: "20px", transition: "background 0.15s", flexShrink: 0,
          }}
        >
          🔔
          {totalCount > 0 && (
            <span style={{
              position: "absolute", top: "4px", right: "4px",
              background: "#ef4444", color: "#fff", fontSize: "10px", fontWeight: "700",
              minWidth: "18px", height: "18px", borderRadius: "9px",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "0 4px", lineHeight: 1, boxShadow: "0 0 0 2px #fff",
              animation: "zeno-pulse 2s infinite",
            }}>
              {totalCount > 99 ? "99+" : totalCount}
            </span>
          )}
        </button>

        {/* DROPDOWN */}
        {open && createPortal(
          <div id="zeno-notif-dropdown" className="zeno-notif-dropdown">

            {/* Header */}
            <div style={{
              padding: "14px 16px 10px", borderBottom: "1px solid #f1f5f9",
              display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
            }}>
              <span style={{ fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                Notifications
                {totalCount > 0 && (
                  <span style={{
                    marginLeft: "8px", background: "#ef4444", color: "#fff",
                    fontSize: "11px", fontWeight: "700", padding: "2px 7px", borderRadius: "10px",
                  }}>{totalCount}</span>
                )}
              </span>
              <div style={{ display: "flex", gap: "6px" }}>
                <button onClick={() => refresh()} title="Refresh"
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#64748b", padding: "2px 6px", borderRadius: "6px" }}>
                  ↻
                </button>
                <button onClick={() => setOpen(false)}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#94a3b8", padding: "2px 6px" }}>
                  ✕
                </button>
              </div>
            </div>

            {/* List */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {notifications.length === 0 ? (
                <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                  <div style={{ fontSize: "28px", marginBottom: "8px" }}>✅</div>
                  All caught up!
                </div>
              ) : (
                <>
                  {/* ACTION REQUIRED section */}
                  {actionItems.length > 0 && (
                    <>
                      <div style={{ padding: "8px 16px 4px", fontSize: "10px", fontWeight: "700", color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.05em", background: "#fef2f2" }}>
                        ⚡ Action Required ({actionItems.length})
                      </div>
                      {actionItems.map((n, i) => renderItem(n, i))}
                    </>
                  )}

                  {/* INFORMATIONAL section */}
                  {infoItems.length > 0 && (
                    <>
                      <div style={{ padding: "8px 16px 4px", fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc" }}>
                        📢 Updates ({infoItems.length})
                      </div>
                      {infoItems.map((n, i) => renderItem(n, i))}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {actionItems.length > 0 && (
              <div style={{ padding: "10px 16px", borderTop: "1px solid #f1f5f9", textAlign: "center", flexShrink: 0 }}>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  {actionItems.length} action{actionItems.length > 1 ? "s" : ""} pending
                </span>
              </div>
            )}
          </div>,
          document.body
        )}
      </div>
    </>
  );
}